import React, { useEffect, useState } from "react";
import { Container, Table, Button, Alert, Spinner, Form, Badge } from "react-bootstrap";
import { Link, Navigate, useNavigate } from "react-router-dom";
import userService from "../../Service/user.service";
import { format } from "date-fns";
import { MdDelete, MdEdit } from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Header from '../../components/HeaderTop/HeaderTop'


const ITEMS_PER_PAGE = 10;

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();

  // Get user role from localStorage
  const user = JSON.parse(localStorage.getItem("user"));
  const token = user?.user_token || null;
  const isAdmin = user?.user_role === 4;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await userService.getAllUsers(token);
        const data = await response.json();
        if (data.status === "success") {
          setUsers(data.data || []);
        } else {
          throw new Error(data.message || "Failed to fetch users");
        }
      } catch (err) {
        setApiError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (isAdmin) fetchUsers();
  }, [isAdmin, token]);

  if (!isAdmin) {
    return <Navigate to="/login" replace />;
  }

  const handleDelete = async (userId) => {
    try {
      const response = await userService.deleteUser(userId, token);
      const data = await response.json();
      if (data.status === "success") {
        setUsers((prev) => prev.filter((user) => user.user_id !== userId));
        toast.success("User deleted successfully");
      } else {
        throw new Error("Failed to delete user");
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filteredUsers = users.filter((u) =>
    u.user_email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const currentUsers = filteredUsers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const formatDate = (dateString) =>
    dateString ? format(new Date(dateString), "MMMM dd, yyyy") : "N/A";

  const getRoleVariant = (role) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "success";
      case "employee":
        return "warning";
      case "user":
        return "danger";
      case "manager":
        return "primary";
      default:
        return "secondary";
    }
  };

  return (
    <>
    <Header/>
    <Container className="mt-5">
     
      <ToastContainer />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>User Management</h2>
        <Button as={Link} to="/d2f4b9a1-7c38-4d7e-ae1e-938b33d8b75e" variant="secondary">
          Order Management
        </Button>
      </div>

      <Form.Group className="mb-4">
        <Form.Label>Search by Email</Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter email"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Form.Group>

      {loading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" />
          <p>Loading users...</p>
        </div>
      ) : apiError ? (
        <Alert variant="danger">{apiError}</Alert>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead className="bg-primary text-white">
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.map((user) => (
                <tr key={user.user_id}>
                  <td>{user.user_firstName}</td>
                  <td>{user.user_lastName}</td>
                  <td>{user.user_email}</td>
                  <td>
                    <Badge bg={getRoleVariant(user.company_role)}>
                      {user.company_role}
                    </Badge>
                  </td>
                  <td>{formatDate(user.CreatedAt)}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => navigate(`/d2f4b9a1-7c38-4d7e-ae1e-938b33d8b75e/user/edit/${user.user_id}`)}
                    >
                      <MdEdit />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(user.user_id)}
                    >
                      <MdDelete />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="d-flex justify-content-center">
            <Button
              variant="outline-primary"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="me-2"
            >
              Previous
            </Button>
            <span className="align-self-center mx-3">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline-primary"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </>
      )}
    </Container>
    </>
  );
};

export default UserList;
